import React, { useRef } from "react";
import {
  DayPicker,
  getDefaultClassNames,
  type DayPickerProps,
} from "react-day-picker";
import { ar } from "react-day-picker/locale";
import "react-day-picker/style.css";
import { useVirtualizer } from "@tanstack/react-virtual";
import dayjs from "dayjs";

import { useLocaleInfo } from "@/app/lib/hooks/useLocaleInfo";
import {
  addMonths,
  formatWeekdayName,
  formatMonthCaption,
  MONTH_ROW_HEIGHT_PX,
  monthDiffInclusive,
} from "./helpers";
import "./calendar.css";

type MonthRowProps = {
  virtualRow: { index: number; start: number; key: React.Key };
  startMonth: Date;
  pickerProps: DayPickerProps;
  measureElement: (el: HTMLElement | null) => void;
};

const MonthRow = ({
  virtualRow,
  startMonth,
  pickerProps,
  measureElement,
}: MonthRowProps) => {
  const month = addMonths(startMonth, virtualRow.index);

  return (
    <div ref={measureElement} data-index={virtualRow.index}>
      <DayPicker
        {...pickerProps}
        numberOfMonths={1}
        month={month}
        hideNavigation
        hideWeekdays
      />
    </div>
  );
};

const WeekDaysLabels = () => {
  const weekdaysLabels = dayjs.weekdaysShort(true);

  return (
    <div className="flex justify-center border-b border-stroke-neutral-soft pb-2">
      {weekdaysLabels.map((label) => (
        <div
          key={label}
          className="w-[var(--rdp-day-width)] px-2 py-0.5 text-center text-sm font-medium text-text-placeholder"
        >
          {label}
        </div>
      ))}
    </div>
  );
};

export function CalendarBase({
  startMonth,
  endMonth,
  classNames,
  numberOfMonths,
  ...props
}: DayPickerProps) {
  const { locale, dir } = useLocaleInfo();
  const defaultClassNames = getDefaultClassNames();
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const scrollable = Boolean(numberOfMonths && numberOfMonths > 1);
  const resolvedStart = startMonth || new Date(1940, 1);
  const resolvedEnd = endMonth || new Date(2050, 1);

  const pickerProps: DayPickerProps = {
    locale: locale === "ar" ? ar : undefined,
    dir,
    formatters: {
      formatWeekdayName,
      formatMonthCaption,
    },
    startMonth: resolvedStart,
    endMonth: resolvedEnd,
    classNames: {
      day: `${defaultClassNames.day} text-text-primary font-medium`,
      chevron: "fill-text-interactive",
      month_caption: `${defaultClassNames.month_caption} text-text-title text-lg font-bold text-center ${
        scrollable ? "!h-6 mt-4" : ""
      }`,
      weekday: `${defaultClassNames.weekday} text-text-placeholder`,
      selected: "selected-day",
      ...classNames,
    },
    navLayout: scrollable ? "after" : "around",
    ...props,
  };

  const monthsToRender = numberOfMonths ?? 1;
  const monthsInRange = monthDiffInclusive(resolvedStart, resolvedEnd);
  const rowCount = Math.min(monthsToRender, monthsInRange);

  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => wrapperRef.current ?? null,
    estimateSize: () => MONTH_ROW_HEIGHT_PX,
    overscan: 3,
    enabled: !!wrapperRef.current,
  });

  if (!scrollable) {
    return (
      <div className="rdp-root">
        <div
          className="flex flex-col items-center justify-center"
          data-sentry-block
        >
          <DayPicker {...pickerProps} />
        </div>
      </div>
    );
  }

  return (
    <div className="rdp-root flex min-h-0 shrink grow basis-auto flex-col">
      <WeekDaysLabels />
      <div
        ref={wrapperRef}
        className="mb-[calc(var(--drawer-height,0)-var(--drawer-bottom-padding,0))] min-h-0 shrink grow basis-auto overflow-y-auto"
      >
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            position: "relative",
          }}
        >
          {rowVirtualizer.getVirtualItems().map((item) => (
            <div
              key={item.key}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${item.size}px`,
                transform: `translateY(${item.start}px)`,
              }}
            >
              <MonthRow
                virtualRow={{
                  index: item.index,
                  start: item.start,
                  key: item.key,
                }}
                startMonth={resolvedStart}
                pickerProps={pickerProps}
                measureElement={rowVirtualizer.measureElement}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
